import { JSX, ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import menuItems from 'menu-items';
import { hasReadPermission, PERMISSION_VIEW } from 'utils/permissions';
import { useGetPermissions, useHasAuthorization } from 'api/authUser';

// types
import { NavItemType } from 'types/menu';
import useAuth from 'hooks/useAuth';

interface PermissionGuardProps {
  children: ReactNode;
};

function normalizePath(path: string | undefined): string {
  if (!path) return '';
  // remove trailing slash (except root)
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

function matchUrl(targetPathname: string, itemUrl: string): boolean {
  const cleanTarget = normalizePath(targetPathname.toLowerCase());
  const cleanItem = normalizePath(itemUrl.toLowerCase());

  if (!cleanItem) return false;

  // If item contains route params like ":id", compare static prefix before first param
  const paramIndex = cleanItem.indexOf('/:');
  if (paramIndex !== -1) {
    const staticPrefix = cleanItem.substring(0, paramIndex);
    return cleanTarget.startsWith(staticPrefix);
  }

  return cleanTarget === cleanItem;
}

function findFeatureIdByPath(pathname: string, nodes: NavItemType[]): number | undefined {
  for (const node of nodes) {
    if (node.url && matchUrl(pathname, node.url)) {
      return node.featureID as number | undefined;
    }
    const children = (node.children as NavItemType[] | undefined) || (node.elements as NavItemType[] | undefined);
    if (children && children.length) {
      const found = findFeatureIdByPath(pathname, children);
      if (typeof found === 'number') return found;
    }
  }
  return undefined;
}

export default function PermissionGuard({ children }: PermissionGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  // Always call hooks at the top level; the hook will no-op when logged out
  const { permissions, isLoading: permissionsLoading } = useGetPermissions();
  const [isCheckingPermission, setIsCheckingPermission] = useState(false);

  const featureId = useMemo(() => {
    const items = (menuItems.items || []) as NavItemType[];
    return findFeatureIdByPath(location.pathname, items);
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoggedIn || permissionsLoading) return;
    const checkPermission = async () => {
      // If route has a feature binding, check permission
      if (typeof featureId === 'number') {
        setIsCheckingPermission(true);
        
        try {
          // First check local permissions (faster)
          const localAllowed = hasReadPermission(featureId, permissions);
          if (!localAllowed) {
            // If local check fails, do a real-time API check
            const apiAllowed = await useHasAuthorization(featureId, PERMISSION_VIEW);
            if (!apiAllowed) {
              navigate('/no-permission', { replace: true });
            }
          }
        } catch (error) {       
          navigate('/no-permission', { replace: true });
        } finally {
          setIsCheckingPermission(false);
        }
      }
    };

    checkPermission();
  }, [isLoggedIn, featureId, permissions, navigate, permissionsLoading]);

  // Show loading state while checking permissions or waiting for initial permissions fetch
  if (!isLoggedIn || isCheckingPermission || permissionsLoading) {
    return <div></div> as JSX.Element;
  }

  return children as JSX.Element;
}


